import React from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useIntl, defineMessages } from 'react-intl';
import { connect } from 'react-redux';

import classNames from 'classnames';

import { Box } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import { VideoRoom } from '../../lib/api/videoroom';
import { State } from '../../lib/reducers';
import {
  isLessonLayout,
  amModerator,
} from '../../lib/reduxSelectors/room';
import { inMeeting } from '../../lib/reduxSelectors/videoRoom';
import { getWsUserId } from '../../lib/reduxSelectors/websocket';
import { IconUsers, IconMicrophone } from '../IconSet';


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    center: {
      textAlign: 'center',
      color: theme.palette.text.primary,
    },
    container: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center'
    },
    onlyAudio: {
      marginLeft: theme.spacing(1.5),
    }
  })
);


const messages = defineMessages({
  meetingParticipantsSummary: { id: 'meetingParticipantsSummary' },
  meetingParticipantsSummaryAudioOnly: { id: 'meetingParticipantsSummaryAudioOnly' },
  meetingParticipantsSummaryAudioOnlyMobile: { id: 'meetingParticipantsSummaryAudioOnlyMobile' },
});


function ParticipantsCount(props: ExtendedProps) {
  const { usersCount, usersWithAudioOnly, inMeeting, isLessonLayout, amIModerator } = props;
  const onlyAudio = usersWithAudioOnly > 0;

  const { formatMessage } = useIntl();

  const classes = useStyles();

  let shouldBeHidden = false;
  if (isLessonLayout && !amIModerator) {
    shouldBeHidden = true;
  }

  const getParticipants = () => {
    let text = formatMessage(messages.meetingParticipantsSummary, { totalUsers: usersCount });
    if (onlyAudio) {
      text += formatMessage(messages.meetingParticipantsSummaryAudioOnly, { audioOnlyUsers: usersWithAudioOnly });
    }
    return (
      <Typography variant="body2" className={classes.center}>
        {text}
      </Typography>
    );
  };

  const getOnlyAudioMobile = () => {
    if (onlyAudio) {
      return (
        <Box p={0} className={classes.onlyAudio}>
          (<IconMicrophone size={20} /> {usersWithAudioOnly})
        </Box>
      );
    } else {
      return null;
    }
  };

  const getParticipantsMobile = () => {
    return (
      <Box p={0} className={classNames(classes.container, classes.center)}>
        <IconUsers size={20} /> {usersCount}
        {getOnlyAudioMobile()}
      </Box>
    );
  };

  if (!inMeeting || shouldBeHidden) {
    return null;
  }

  return (
    <React.Fragment>
      { isMobileOnly ? getParticipantsMobile() : getParticipants() }
    </React.Fragment>
  );
}


type MappedProps = {
  usersCount: number;
  usersWithAudioOnly: number;
  inMeeting: boolean;
  isLessonLayout: boolean;
  amIModerator: boolean;
}


type ExtendedProps = {} & MappedProps;

const mapStateToProps = (state: State): MappedProps => {
  const roster = state.room.roster;
  const myUserId = getWsUserId(state.websocket);
  const audioOnlyStreamsCount = Object.keys(state.room.roster).reduce(
    (audioCount, uid: string) => {
      if (uid === myUserId) {
        const iHaveVideo = VideoRoom.getVideoTrackFromStream(state.room.localvideo_stream) && !roster[uid].isVideoMuted;
        return iHaveVideo ? audioCount : audioCount + 1;
      }
      else if (VideoRoom.getVideoTrackFromStream(roster[uid].stream)) {
        return audioCount;
      }
      else {
        return audioCount + 1;
      }
    }
    , 0
  );
  return {
    usersCount: Object.keys(state.room.roster).length,
    usersWithAudioOnly: audioOnlyStreamsCount,
    inMeeting: inMeeting(state),
    isLessonLayout: isLessonLayout(state),
    amIModerator: amModerator(state),
  };
};


export default connect(mapStateToProps)(ParticipantsCount);
